import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
  Slider,
  Chip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { MoviePoster } from '@aperture/ui'
import { useUserRatings } from '../hooks/useUserRatings'

interface Movie {
  id: string
  title: string
  year: number | null
  genres: string[]
  overview: string | null
  poster_url: string | null
  community_rating: number | null
}

interface Collection {
  name: string
  count: number
}

export function MoviesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getRating, setRating } = useUserRatings()
  const [movies, setMovies] = useState<Movie[]>([])
  const [genres, setGenres] = useState<string[]>([])
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [genre, setGenre] = useState('')
  const [collection, setCollection] = useState('')
  const [minRtScore, setMinRtScore] = useState<number>(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const pageSize = 24

  const handleRate = useCallback(
    async (movieId: string, rating: number | null) => {
      try {
        await setRating('movie', movieId, rating)
      } catch (err) {
        console.error('Failed to rate movie:', err)
      }
    },
    [setRating]
  )

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [genresRes, collectionsRes] = await Promise.all([
          fetch('/api/movies/genres', { credentials: 'include' }),
          fetch('/api/movies/collections', { credentials: 'include' }),
        ])
        if (genresRes.ok) {
          const data = await genresRes.json()
          setGenres(data.genres)
        }
        if (collectionsRes.ok) {
          const data = await collectionsRes.json()
          setCollections(data.collections)
        }
      } catch {
        // Ignore filter fetch errors
      }
    }
    fetchFilters()
  }, [])

  useEffect(() => {
    const fetchMovies = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        })
        if (search) params.set('search', search)
        if (genre) params.set('genre', genre)
        if (collection) params.set('collection', collection)
        if (minRtScore > 0) params.set('minRtScore', String(minRtScore))

        const response = await fetch(`/api/movies?${params}`, { credentials: 'include' })
        if (response.ok) {
          const data = await response.json()
          setMovies(data.movies)
          setTotalPages(Math.ceil(data.total / pageSize))
          setError(null)
        } else {
          setError(t('browse.movies.errorLoad'))
        }
      } catch {
        setError(t('browse.movies.errorConnect'))
      } finally {
        setLoading(false)
      }
    }

    const debounce = setTimeout(fetchMovies, search ? 300 : 0)
    return () => clearTimeout(debounce)
  }, [page, search, genre, collection, minRtScore, t])

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={1}>
        {t('browse.movies.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        {t('browse.movies.subtitle')}
      </Typography>

      {/* Filters */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap" alignItems="center">
        <TextField
          placeholder={t('browse.movies.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          size="small"
          sx={{ width: { xs: '100%', sm: 250 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ width: { xs: '100%', sm: 150 } }}>
          <InputLabel>{t('browse.genre')}</InputLabel>
          <Select
            value={genre}
            label={t('browse.genre')}
            onChange={(e) => {
              setGenre(e.target.value)
              setPage(1)
            }}
          >
            <MenuItem value="">{t('browse.movies.allGenres')}</MenuItem>
            {genres.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {collections.length > 0 && (
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 180 } }}>
            <InputLabel>{t('browse.movies.franchise')}</InputLabel>
            <Select
              value={collection}
              label={t('browse.movies.franchise')}
              onChange={(e) => {
                setCollection(e.target.value)
                setPage(1)
              }}
            >
              <MenuItem value="">{t('browse.movies.allFranchises')}</MenuItem>
              {collections.map((c) => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name} ({c.count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ width: { xs: '100%', sm: 180 }, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('browse.minRtScore', {
              value: minRtScore > 0 ? `${minRtScore}%` : t('browse.any'),
            })}
          </Typography>
          <Slider
            value={minRtScore}
            onChange={(_, value) => {
              setMinRtScore(value as number)
              setPage(1)
            }}
            min={0}
            max={100}
            step={10}
            size="small"
            sx={{ mt: -0.5 }}
          />
        </Box>

        {/* Active filters display */}
        {(genre || collection || minRtScore > 0) && (
          <Box display="flex" gap={0.5} alignItems="center">
            {genre && (
              <Chip
                label={genre}
                size="small"
                onDelete={() => { setGenre(''); setPage(1) }}
              />
            )}
            {collection && (
              <Chip
                label={collection}
                size="small"
                onDelete={() => { setCollection(''); setPage(1) }}
              />
            )}
            {minRtScore > 0 && (
              <Chip
                label={t('browse.rtChip', { min: minRtScore })}
                size="small"
                onDelete={() => { setMinRtScore(0); setPage(1) }}
              />
            )}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 12 }).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
              <MoviePoster title="" loading responsive />
            </Grid>
          ))}
        </Grid>
      ) : movies.length === 0 ? (
        <Typography color="text.secondary">
          {search || genre || collection || minRtScore > 0
            ? t('browse.movies.emptyFiltered')
            : t('browse.movies.emptyNoSync')}
        </Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            {movies.map((movie) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={movie.id}>
                <MoviePoster
                  title={movie.title}
                  year={movie.year}
                  posterUrl={movie.poster_url}
                  rating={movie.community_rating}
                  genres={movie.genres}
                  overview={movie.overview}
                  userRating={getRating('movie', movie.id)}
                  onRate={(rating) => handleRate(movie.id, rating)}
                  responsive
                  onClick={() => navigate(`/movies/${movie.id}`)}
                />
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

